import React from "react";
import { Mail, Globe } from "lucide-react";

const PrivacyPolicy: React.FC = () => {

  return (
    <div className="min-h-screen bg-white">

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-12">
        {/* Title Section */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-[#171923] mb-4">
            JobMeIn – Privacy Policy
          </h1>
          <p className="text-[#718096] text-lg">Last updated: January 11, 2026</p>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 my-8"></div>

        {/* Section 1: Introduction */}
        <section className="mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-[#171923] mb-4">
            1. Introduction
          </h2>
          <div className="space-y-4 text-[#4A5568] leading-relaxed">
            <p>
              JobMeIn ("JobMeIn", "we", "us") respects your privacy.
            </p>
            <p>
              This Privacy Policy explains how we collect, use, store, and protect your personal data when you use the JobMeIn mobile application.
            </p>
            <p>
              By using JobMeIn, you agree to this Privacy Policy.
            </p>
            <p>
              JobMeIn is a global platform headquartered in the United Arab Emirates, respecting local data protection and privacy laws wherever users are located.
            </p>
          </div>
        </section>

        {/* Divider */}
        <div className="border-t border-gray-200 my-8"></div>

        {/* Section 2: Personal Data We Collect */}
        <section className="mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-[#171923] mb-6">
            2. Personal Data We Collect
          </h2>
          <p className="text-[#4A5568] mb-6 leading-relaxed">
            We may collect the following categories of personal data:
          </p>

          <div className="space-y-8">
            {/* a) Identity & Contact Information */}
            <div className="bg-[#F7FAFC] rounded-md p-6 border border-gray-100">
              <h3 className="text-xl font-semibold text-[#171923] mb-4">
                a) Identity & Contact Information
              </h3>
              <ul className="space-y-2 text-[#4A5568] list-disc list-inside">
                <li>First and last name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Country of residence</li>
              </ul>
            </div>

            {/* b) Profile & Professional Information */}
            <div className="bg-[#F7FAFC] rounded-md p-6 border border-gray-100">
              <h3 className="text-xl font-semibold text-[#171923] mb-4">
                b) Profile & Professional Information
              </h3>
              <ul className="space-y-2 text-[#4A5568] list-disc list-inside">
                <li>Skills (technical and soft skills)</li>
                <li>Work experience, internships, and projects</li>
                <li>Education, certifications, and training</li>
                <li>Industries, roles, and job preferences</li>
              </ul>
            </div>

            {/* c) Documents & User-Provided Content */}
            <div className="bg-[#F7FAFC] rounded-md p-6 border border-gray-100">
              <h3 className="text-xl font-semibold text-[#171923] mb-4">
                c) Documents & User-Provided Content
              </h3>
              <ul className="space-y-2 text-[#4A5568] list-disc list-inside">
                <li>CVs or resumes</li>
                <li>Portfolios, links, and attachments</li>
                <li>Achievements, references, and descriptions you choose to share</li>
              </ul>
            </div>

            {/* d) Application & Activity Information */}
            <div className="bg-[#F7FAFC] rounded-md p-6 border border-gray-100">
              <h3 className="text-xl font-semibold text-[#171923] mb-4">
                d) Application & Activity Information
              </h3>
              <ul className="space-y-2 text-[#4A5568] list-disc list-inside">
                <li>Jobs viewed or applied for</li>
                <li>Application status and history</li>
                <li>Profile visibility and interactions</li>
              </ul>
            </div>

            {/* e) Technical & Usage Information */}
            <div className="bg-[#F7FAFC] rounded-md p-6 border border-gray-100">
              <h3 className="text-xl font-semibold text-[#171923] mb-4">
                e) Technical & Usage Information
              </h3>
              <ul className="space-y-2 text-[#4A5568] list-disc list-inside">
                <li>App usage activity</li>
                <li>Device type and operating system</li>
                <li>App version and performance data</li>
              </ul>
            </div>

            {/* f) Location Information */}
            <div className="bg-[#F7FAFC] rounded-md p-6 border border-gray-100">
              <h3 className="text-xl font-semibold text-[#171923] mb-4">
                f) Location Information
              </h3>
              <ul className="space-y-2 text-[#4A5568] list-disc list-inside">
                <li>Country and city (if provided)</li>
              </ul>
              <p className="text-[#718096] text-sm mt-3 italic">
                (Exact or real-time location tracking is not used.)
              </p>
            </div>

            {/* g) Payment & Subscription Information */}
            <div className="bg-[#F7FAFC] rounded-md p-6 border border-gray-100">
              <h3 className="text-xl font-semibold text-[#171923] mb-4">
                g) Payment & Subscription Information
              </h3>
              <ul className="space-y-2 text-[#4A5568] list-disc list-inside">
                <li>Subscription type</li>
                <li>Payment status</li>
                <li>Transaction references</li>
              </ul>
              <p className="text-[#718096] text-sm mt-3 italic">
                (Payments are processed by third-party providers. JobMeIn does not store card or banking information.)
              </p>
            </div>

            {/* h) Communications & Support */}
            <div className="bg-[#F7FAFC] rounded-md p-6 border border-gray-100">
              <h3 className="text-xl font-semibold text-[#171923] mb-4">
                h) Communications & Support
              </h3>
              <ul className="space-y-2 text-[#4A5568] list-disc list-inside">
                <li>Messages sent to customer support</li>
                <li>Feedback, inquiries, or issue reports</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="border-t border-gray-200 my-8"></div>

        {/* Section 3: How We Use Your Personal Data */}
        <section className="mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-[#171923] mb-4">
            3. How We Use Your Personal Data
          </h2>
          <p className="text-[#4A5568] mb-4 leading-relaxed">
            We use personal data to:
          </p>
          <ul className="space-y-2 text-[#4A5568] list-disc list-inside ml-4">
            <li>Create and manage user accounts</li>
            <li>Enable job matching and applications</li>
            <li>Allow employers to view candidate profiles</li>
            <li>Provide paid features and subscriptions</li>
            <li>Improve platform performance and user experience</li>
            <li>Comply with legal and regulatory obligations</li>
          </ul>
        </section>

        {/* Divider */}
        <div className="border-t border-gray-200 my-8"></div>

        {/* Section 4: Sharing of Personal Data */}
        <section className="mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-[#171923] mb-4">
            4. Sharing of Personal Data
          </h2>
          <div className="space-y-4 text-[#4A5568] leading-relaxed">
            <p>
              JobMeIn does not sell, rent, or trade personal data.
            </p>
            <p>
              Personal data is shared with employers only for recruitment purposes, and only when:
            </p>
            <ul className="space-y-2 list-disc list-inside ml-4">
              <li>A candidate applies for a job, or</li>
              <li>A candidate chooses to make their profile visible</li>
            </ul>
          </div>
        </section>

        {/* Divider */}
        <div className="border-t border-gray-200 my-8"></div>

        {/* Section 5: Aggregated & Anonymized Data */}
        <section className="mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-[#171923] mb-4">
            5. Aggregated & Anonymized Data
          </h2>
          <div className="space-y-4 text-[#4A5568] leading-relaxed">
            <p>
              JobMeIn may use aggregated and anonymized data that does not identify individuals for:
            </p>
            <ul className="space-y-2 list-disc list-inside ml-4">
              <li>Analytics and reporting</li>
              <li>Market insights</li>
              <li>Platform improvement</li>
            </ul>
            <p>
              This data cannot be used to identify any user.
            </p>
          </div>
        </section>

        {/* Divider */}
        <div className="border-t border-gray-200 my-8"></div>

        {/* Section 6: Data Storage & Third-Party Services */}
        <section className="mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-[#171923] mb-4">
            6. Data Storage & Third-Party Services
          </h2>
          <div className="space-y-4 text-[#4A5568] leading-relaxed">
            <p>
              Personal data may be stored and processed using trusted third-party service providers (such as cloud hosting, analytics, and payment processors).
            </p>
            <p>
              Data may be processed outside your country of residence in compliance with applicable data-protection laws.
            </p>
          </div>
        </section>

        {/* Divider */}
        <div className="border-t border-gray-200 my-8"></div>

        {/* Section 7: International Data Processing & Applicable Laws */}
        <section className="mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-[#171923] mb-4">
            7. International Data Processing & Applicable Laws
          </h2>
          <div className="space-y-4 text-[#4A5568] leading-relaxed">
            <p>
              JobMeIn operates globally and provides its services to users located in different countries.
            </p>
            <p>
              Personal data may be stored and processed using secure cloud infrastructure located in the United Arab Emirates (including the Middle East).
            </p>
            <p>
              JobMeIn processes personal data in accordance with applicable data protection and privacy laws based on the location of the user.
            </p>
            <p>
              Where international data transfers occur, JobMeIn applies appropriate technical and organizational safeguards to ensure personal data is handled securely and lawfully.
            </p>
            <p>
              Where users are located in jurisdictions that grant specific data-protection rights, those rights will be respected.
            </p>
          </div>
        </section>

        {/* Divider */}
        <div className="border-t border-gray-200 my-8"></div>

        {/* Section 8: Data Retention */}
        <section className="mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-[#171923] mb-4">
            8. Data Retention
          </h2>
          <div className="space-y-4 text-[#4A5568] leading-relaxed">
            <p>We retain personal data:</p>
            <ul className="space-y-2 list-disc list-inside ml-4">
              <li>For as long as your account remains active, or</li>
              <li>As required to meet legal obligations</li>
            </ul>
            <p>
              You may request deletion of your data at any time.
            </p>
          </div>
        </section>

        {/* Divider */}
        <div className="border-t border-gray-200 my-8"></div>

        {/* Section 9: Your Rights */}
        <section className="mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-[#171923] mb-4">
            9. Your Rights
          </h2>
          <div className="space-y-4 text-[#4A5568] leading-relaxed">
            <p>You have the right to:</p>
            <ul className="space-y-2 list-disc list-inside ml-4">
              <li>Access your personal data</li>
              <li>Correct inaccurate or incomplete data</li>
              <li>Request deletion of your data</li>
              <li>Withdraw consent where applicable</li>
            </ul>
            <p className="mt-4">
              Requests can be sent to:
            </p>
            <a
              href="mailto:tech@jobmein.com"
              className="inline-flex items-center gap-2 text-[#1C4532] hover:text-[#ABD405] transition-colors font-medium"
            >
              <Mail className="w-4 h-4" />
              tech@jobmein.com
            </a>
          </div>
        </section>

        {/* Divider */}
        <div className="border-t border-gray-200 my-8"></div>

        {/* Section 10: Data Security */}
        <section className="mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-[#171923] mb-4">
            10. Data Security
          </h2>
          <div className="space-y-4 text-[#4A5568] leading-relaxed">
            <p>
              We apply reasonable technical and organizational measures to protect personal data.
            </p>
            <p>
              However, no system can be guaranteed to be completely secure.
            </p>
          </div>
        </section>

        {/* Divider */}
        <div className="border-t border-gray-200 my-8"></div>

        {/* Section 11: Changes to This Privacy Policy */}
        <section className="mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-[#171923] mb-4">
            11. Changes to This Privacy Policy
          </h2>
          <div className="space-y-4 text-[#4A5568] leading-relaxed">
            <p>
              We may update this Privacy Policy from time to time.
            </p>
            <p>
              Continued use of the app means acceptance of the updated policy.
            </p>
          </div>
        </section>

        {/* Divider */}
        <div className="border-t border-gray-200 my-8"></div>

        {/* Section 12: Governing Law */}
        <section className="mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-[#171923] mb-4">
            12. Governing Law
          </h2>
          <div className="space-y-4 text-[#4A5568] leading-relaxed">
            <p>
              This Privacy Policy is governed by the laws of the United Arab Emirates.
            </p>
            <p>
              Mandatory data-protection rights applicable based on a user's location will be respected.
            </p>
          </div>
        </section>

        {/* Divider */}
        <div className="border-t border-gray-200 my-8"></div>

        {/* Contact Section */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-[#171923] mb-6">
            Contact Us
          </h2>
          <div className="space-y-4">
            <a
              href="mailto:tech@jobmein.com"
              className="flex items-center gap-3 text-[#1C4532] hover:text-[#ABD405] transition-colors group"
            >
              <Mail className="w-5 h-5" />
              <span className="font-medium group-hover:underline">tech@jobmein.com</span>
            </a>
            <a
              href="https://www.jobmein.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-[#1C4532] hover:text-[#ABD405] transition-colors group"
            >
              <Globe className="w-5 h-5" />
              <span className="font-medium group-hover:underline">www.jobmein.com</span>
            </a>
          </div>
        </section>
      </div>

      {/* Footer spacing */}
      <div className="h-16"></div>
    </div>
  );
};

export default PrivacyPolicy;

